import { Modal } from "antd";
import React, { useEffect, useRef, useState } from "react";
import * as S from "./style";
import DSButton from "../DSButton";
import api from "../../services/api";
import { notifyError } from "../../utils/notifications";
import LoadingElevaty from "../LoadingElevaty";

type InsuranceAgreementModalProps = {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  contractId: string;
};

export function InsuranceAgreementModal({
  isOpen,
  onClose,
  clientId,
  contractId,
}: InsuranceAgreementModalProps) {
  const [isFetching, setIsFetching] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [insuranceHtml, setInsuranceHtml] = useState<null | string>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const handlePrint = () => {
    setIsPrinting(true);
  };

  useEffect(() => {
    const fetchInsuranceAgreement = async () => {
      if (!isOpen) {
        setInsuranceHtml(null);
        setIsPrinting(false);
        return;
      }
      try {
        setIsFetching(true);
        const resp = await api.get(
          "insurance_contracts",
          { client_id: clientId, contract_id: contractId },
          { "Content-Type": "application/json" },
          true
        );

        const backendHtmlString = await resp.text();

        setInsuranceHtml(backendHtmlString);
      } catch (e) {
        notifyError("Erro ao buscar o termo de adesão", "", false, true);
        onClose();
      } finally {
        setIsFetching(false);
      }
    };
    fetchInsuranceAgreement();
  }, [isOpen, clientId, contractId]);

  useEffect(() => {
    if (!isPrinting) {
      return;
    }

    const iframe = iframeRef.current;
    const printWindow = iframe?.contentWindow;

    if (!printWindow) {
      notifyError(
        "Não foi possível encontrar o termo de adesão para a impressão",
        "",
        false,
        true
      );
      setIsPrinting(false);
      return;
    }

    let isPrintJobFinished = false;

    const finishPrintJob = () => {
      if (isPrintJobFinished) {
        return;
      }
      isPrintJobFinished = true;

      setTimeout(() => {
        setIsPrinting(false);
      }, 0);
    };

    const handleAfterPrint = () => {
      finishPrintJob();
    };
    const handleMediaChange = (mql: MediaQueryListEvent) => {
      if (!mql.matches) {
        finishPrintJob();
      }
    };

    printWindow.addEventListener("afterprint", handleAfterPrint);
    const mediaQueryList = printWindow.matchMedia?.("print");
    mediaQueryList?.addEventListener("change", handleMediaChange);
    const failsafeTimeout = setTimeout(() => {
      finishPrintJob();
    }, 5000);

    printWindow.focus();
    printWindow.print();

    return () => {
      finishPrintJob();

      clearTimeout(failsafeTimeout);
      printWindow.removeEventListener("afterprint", handleAfterPrint);
      mediaQueryList?.removeEventListener("change", handleMediaChange);
    };
  }, [isPrinting]);

  return (
    <Modal
      title="Termo de adesão"
      onCancel={onClose}
      width={800}
      open={isOpen}
      destroyOnClose
    >
      {isFetching ? (
        <S.WrapperNewLoading>
          <LoadingElevaty />
        </S.WrapperNewLoading>
      ) : (
        <iframe
          ref={iframeRef}
          srcDoc={insuranceHtml || ""}
          title="Termo de Adesão do Seguro"
          style={{
            width: "100%",
            height: "400px",
            border: "none",
          }}
        />
      )}
      <S.ButtonsWrapper>
        <DSButton
          type="reset"
          buttonText="Cancelar"
          buttonType="secondary"
          isCancelType
          height={30}
          style={{
            padding: "0.4rem 1.5rem",
          }}
          isDisabled={isFetching || isPrinting}
          disabled={isFetching || isPrinting}
          onClick={onClose}
        />
        <DSButton
          type="button"
          buttonText="Imprimir"
          buttonType="secondary"
          height={30}
          style={{
            padding: "0.4rem 1.5rem",
          }}
          isDisabled={isFetching || isPrinting}
          disabled={isFetching || isPrinting}
          isLoading={isPrinting}
          onClick={handlePrint}
        />
      </S.ButtonsWrapper>
    </Modal>
  );
}
